import { useChapterHandler } from "@/contexts/chapterHandler";
import { Button } from "./ui/button";
import { TaskType } from "@/types/content-types";
import { ScrollTargetId } from "@/hooks/useScroller";
import { useStepHandler } from "@/contexts/stepHandler";
import { useTask } from "@/contexts/appContent/appContentHooks";
import { useCurrentTask } from "@/contexts/currentTask/useCurrentTask";


export type OpenTaskButtonProps = {
    label: string;
    task: TaskType;
    scrollTarget?: ScrollTargetId;
};

export function OpenTaskButton({ label, task, scrollTarget }: OpenTaskButtonProps) {
    const { setOpenTask } = useChapterHandler();
    return (
        <Button
            variant="step-current"
            size="default-grow"
            onClick={() => {
                setOpenTask(task, scrollTarget);
            }}
        >
            {label}
        </Button>
    );
}

export type ContinueJourneyButtonProps = {
    label: string;
    className?: string;
};
export function ContinueJourneyButton({ label, className }: ContinueJourneyButtonProps) {
    const { setCurrentSectionId, currentSection, setOpenTask, openTask, chapter: currentChapter } = useChapterHandler();
    const { setStepNumber, currentStep, setChapterNumber } = useStepHandler();
    const { save, data: userData } = useTask({ taskId: openTask?.id });
    const currentTask = useCurrentTask({ ignoreError: true });
    // simple_practise tasks are only completed by clicking this button, regardless of inputs
    const completedByButton = openTask?.type === "simple_practise";
    const answers = Object.values(userData?.data ?? {});
    const allInputsFilled = answers.length >= (currentTask?.inputCount ?? 0)
        && answers.every(v => v?.toString().trim().length > 0);
    return (
        <Button
            className={className}
            variant="step-current"
            size="default-grow"
            disabled={
                // simple_practise tasks with inputs require every input to be filled in
                // The input count is unknown until the task has mounted, so stay disabled until then
                (completedByButton && currentTask != null && (currentTask.inputCount == null || (currentTask.inputCount > 0 && !allInputsFilled))) ||
                // If the task has inputs, it will be marked as completed automatically once they are filled, so all tasks need to be completed
                (!completedByButton && currentTask?.inputCount > 0 && currentSection.completedTaskCount < currentSection.taskCount) ||
                // If the task has no inputs, it will not be marked as completed automatically when the task is opened, so all but one
                // task (this buttons task) needs to be completed
                currentSection.completedTaskCount < currentSection.taskCount - 1}
            onClick={() => {
                // Don't override the save state if the task has inputs, since it already must be completed
                if (completedByButton) {
                    // Keep any answers already saved; only mark the task as completed
                    save({
                        completed: true,
                        data: { data: userData?.data, version: 2 },
                    });
                } else if (currentTask?.inputCount === 0) save({
                    completed: true,
                    data: {
                        data: undefined,
                        version: 2,
                    }
                });

                if (currentSection?.section?.next_section) {
                    setCurrentSectionId(currentSection.section.next_section, { skipProgressionCheck: true });
                } else if (currentChapter.next_chapter) {
                    setChapterNumber(currentChapter.number + 1);
                } else {
                    setStepNumber(currentStep.number + 1);
                }
                setOpenTask(null);
            }}
        >
            {label}
        </Button>
    );
}   